import {
  registerReloadBlocker,
  setReloadBlocker,
  type ReloadSafety,
} from '@/lib/reload-blockers';
import { getComposerDraftReloadSafety } from './composer-input/composer-reload-safety';
import { serializeComposerDraft } from './composer-draft-storage';
import { getStoredSessionDraftNativeReloadSafety } from '@/lib/session-draft-native-safety';

/** Capture at most once per interval; unchanged snapshots never rewrite storage. */
export function createComposerDraftWriter(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  key: string,
  delay = 250,
  route?: string
) {
  const blockerId = `org-chat-draft:${key}`;
  const blocker = registerReloadBlocker(blockerId, 'unknown', route);
  let latest: (() => unknown) | null = null;
  let lastCapture: (() => unknown) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let accepted: string | null = null;
  let checkpointNativeSafety: ReloadSafety = 'unknown';
  try {
    accepted = storage.getItem(key);
  } catch {
    /* Storage may be unavailable. */
  }
  const flush = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (!latest) return;
    const capture = latest;
    latest = null;
    try {
      const draft = capture();
      const validated = getComposerDraftReloadSafety(draft) !== 'unknown';
      checkpointNativeSafety = getComposerDraftReloadSafety(draft);
      const serialized = serializeComposerDraft(draft);
      if (serialized !== accepted) storage.setItem(key, serialized);
      accepted = serialized;
      blocker.set(
        () => {
          const current = capture();
          if (!validated || getComposerDraftReloadSafety(current) === 'unknown')
            return 'unknown';
          // Editor refs can contain a new keystroke before React schedules its next write.
          return serializeComposerDraft(current) === accepted
            ? 'safe'
            : 'blocked';
        },
        () => getComposerDraftReloadSafety(capture())
      );
    } catch {
      // Preserve the latest snapshot for pagehide/unmount or a later edit retry.
      latest = capture;
      blocker.set(() => {
        const state = getComposerDraftReloadSafety(capture());
        return state === 'safe' ? 'unknown' : state;
      });
    }
  };
  return {
    schedule(capture: () => unknown) {
      latest = capture;
      lastCapture = capture;
      blocker.set(() => getComposerDraftReloadSafety(capture()));
      // A maximum interval prevents streaming UI renders from starving persistence.
      timer ??= setTimeout(flush, delay);
    },
    flush,
    dispose() {
      latest ??= lastCapture;
      flush();
      // Session checkpoints survive web reload, but nonempty drafts cannot survive
      // a new native WebContents. Keep only their captured native restriction.
      if (!latest) {
        if (checkpointNativeSafety === 'safe') blocker();
        else
          blocker.set(
            checkpointNativeSafety === 'unknown' ? 'unknown' : 'safe',
            () => getStoredSessionDraftNativeReloadSafety(storage.getItem(key))
          );
      }
    },
    cancel() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      latest = null;
      lastCapture = null;
      blocker();
    },
  };
}

const activeWriters = new Map<
  string,
  { flush: () => void; retire: () => void }
>();
/** Navigation checkpoints must include any snapshot still waiting for its timer. */
export function registerComposerDraftWriter(
  key: string,
  flush: () => void,
  retire: () => void = () => {}
) {
  // Exit animations can keep the previous composer mounted during a handoff.
  const previous = activeWriters.get(key);
  previous?.flush();
  previous?.retire();
  const owner = { flush, retire };
  activeWriters.set(key, owner);
  return () => {
    if (activeWriters.get(key) === owner) activeWriters.delete(key);
  };
}
export function flushComposerDraftWriter(key: string) {
  activeWriters.get(key)?.flush();
}

/**
 * The draft for `key` was sent: stop its writer so the composer's unmount
 * flush can't persist the sent text again (e.g. /design navigating away
 * while its editor still shows the prompt).
 */
export function retireComposerDraftWriter(key: string) {
  setReloadBlocker(`org-chat-draft:${key}`, 'safe');
  const owner = activeWriters.get(key);
  if (!owner) return;
  owner.retire();
  activeWriters.delete(key);
}
